import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Calendar, Filter, Download, Users, ChevronDown, ChevronRight, FileSpreadsheet, RefreshCw, X, Mail, Phone, Briefcase, Wallet, MapPin, Cake, CreditCard, User as UserIcon, ShieldCheck, Fingerprint, FileText, Clock as ClockIcon, TrendingUp } from "lucide-react";
import api from "@/lib/api";
import { Card, PageHeader, Button, Field, Input, Select, Badge, Modal } from "@/components/ui-kit";
import { DatePicker } from "@/components/DatePicker";
import { formatIDR } from "@/lib/format";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Iter v34k (#6) — Rekap Absensi per Departemen.
 * Format mirip Excel: NO | Nama Karyawan | Total Hari | Total Jam Lembur | Total Jam Kerja | SAKIT | IZIN | CUTI | ALPHA
 * Grouped per departemen (otomatis bertambah saat departemen baru dibuat).
 */
const monthNames = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];

export default function RekapAbsensi() {
  const { user } = useAuth();
  // Iter v34cm — Detail pop-out selalu tersedia bila user sudah bisa lihat rekap
  // departemen. Perm `attendance.recap_dept_detail` optional (untuk membatasi
  // jika suatu saat perlu granular), tapi default: siapapun yg bisa akses page
  // ini bisa buka detail. Ini mengembalikan fitur pop-out yang sempat hilang.
  const _perms = user?.permissions || [];
  const canViewDetail = _perms.includes("*")
    || _perms.includes("attendance.recap_dept_detail")
    || _perms.includes("attendance.recap_dept")
    || _perms.includes("attendance.recap")
    || (user?.role || "") === "super_admin"
    || (user?.role || "") === "engineer"
    || (user?.role || "") === "admin"
    || (user?.role || "") === "hr";
  const now = new Date();
  const [mode, setMode] = useState("month");  // "month" | "range"
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [filterDept, setFilterDept] = useState("");
  const [collapsed, setCollapsed] = useState({});
  // Iter v34bv (#10) — Popup detail karyawan
  const [detailEmp, setDetailEmp] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const openEmpDetail = async (empId) => {
    if (!canViewDetail) return;
    setDetailLoading(true);
    try {
      const params = {};
      if (mode === "range" && startDate && endDate) {
        params.start_date = startDate; params.end_date = endDate;
      } else { params.year = year; params.month = month; }
      const r = await api.get(`/attendance/employee-detail/${empId}`, { params });
      setDetailEmp(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memuat detail karyawan");
    } finally { setDetailLoading(false); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (mode === "range" && startDate && endDate) {
        params.start_date = startDate;
        params.end_date = endDate;
      } else {
        params.year = year;
        params.month = month;
      }
      if (filterDept) params.department = filterDept;
      const r = await api.get(`/attendance/recap-by-department`, { params });
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memuat rekap");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    api.get("/department-categories").then((r) => setDepartments(r.data || [])).catch(() => setDepartments([]));
  }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [year, month, filterDept, mode, startDate, endDate]);

  const totalEmployees = useMemo(() => (data?.departments || []).reduce((s, d) => s + (d.count || 0), 0), [data]);

  const exportCSV = () => {
    if (!data) return;
    const lines = [];
    const sep = ",";
    (data.departments || []).forEach((d) => {
      lines.push(`Departemen:${sep}${escapeCSV(d.department)}`);
      lines.push(["NO", "Nama Karyawan", "Total Hari", "Total Jam Lembur", "Total Jam Kerja", "SAKIT", "IZIN", "CUTI", "ALPHA"].join(sep));
      d.employees.forEach((e) => {
        lines.push([e.no, escapeCSV(e.name), e.total_hari, e.total_jam_lembur, e.total_jam_kerja, e.sakit, e.izin, e.cuti, e.alpha].join(sep));
      });
      lines.push("");
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `Rekap-Absensi-${data.month}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6" data-testid="rekap-absensi-page">
      <PageHeader overline="HR · Absensi" title="Rekap Absensi per Departemen"
        description="Ringkasan absensi bulanan per departemen. Bertambah otomatis saat departemen baru dibuat." />

      <Card className="p-3 md:p-4 space-y-3">
        <div className="flex gap-2">
          <button onClick={() => setMode("month")} className={`px-3 py-1.5 text-xs uppercase tracking-wider border ${mode === "month" ? "bg-terra-500 text-white border-terra-500" : "bg-white border-stone-300 text-stone-600 hover:bg-stone-50"}`} data-testid="rekap-mode-month">Per Bulan</button>
          <button onClick={() => setMode("range")} className={`px-3 py-1.5 text-xs uppercase tracking-wider border ${mode === "range" ? "bg-terra-500 text-white border-terra-500" : "bg-white border-stone-300 text-stone-600 hover:bg-stone-50"}`} data-testid="rekap-mode-range">Rentang Tanggal</button>
        </div>
        <div className="flex flex-wrap gap-3 items-end">
          {mode === "month" ? (
            <>
              <Field label="Tahun" className="!mb-0">
                <Input type="number" min={2020} max={2099} value={year} onChange={(e) => setYear(parseInt(e.target.value || "0", 10))} className="!w-28" data-testid="rekap-year" />
              </Field>
              <Field label="Bulan" className="!mb-0">
                <Select value={month} onChange={(e) => setMonth(parseInt(e.target.value, 10))} className="!w-40" data-testid="rekap-month">
                  {monthNames.map((n, i) => <option key={i} value={i + 1}>{n}</option>)}
                </Select>
              </Field>
            </>
          ) : (
            <>
              <Field label="Tanggal Dari" className="!mb-0">
                <DatePicker value={startDate} onChange={(e) => setStartDate(e.target.value)} className="!w-44" data-testid="rekap-start-date" />
              </Field>
              <Field label="Tanggal Sampai" className="!mb-0">
                <DatePicker value={endDate} onChange={(e) => setEndDate(e.target.value)} className="!w-44" data-testid="rekap-end-date" />
              </Field>
            </>
          )}
          <Field label="Departemen" className="!mb-0">
            <Select value={filterDept} onChange={(e) => setFilterDept(e.target.value)} className="!w-48" data-testid="rekap-dept-filter">
              <option value="">Semua Departemen</option>
              {departments.map((d) => <option key={d.id} value={d.name}>{d.name}</option>)}
            </Select>
          </Field>
          <div className="flex-1" />
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={load} disabled={loading} data-testid="rekap-refresh">
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />Refresh
            </Button>
            <Button onClick={exportCSV} disabled={!data} data-testid="rekap-export">
              <FileSpreadsheet className="h-3.5 w-3.5" />Export CSV
            </Button>
          </div>
        </div>
      </Card>

      {data && (
        <>
        {/* Bento KPI hero */}
        {(() => {
          const totals = (data.departments || []).reduce((acc, d) => {
            for (const e of (d.employees || [])) {
              acc.sakit += Number(e.sakit) || 0;
              acc.izin += Number(e.izin) || 0;
              acc.cuti += Number(e.cuti) || 0;
              acc.alpha += Number(e.alpha) || 0;
              acc.jam_kerja += Number(e.total_jam_kerja) || 0;
              acc.lembur += Number(e.total_jam_lembur) || 0;
            }
            return acc;
          }, { sakit: 0, izin: 0, cuti: 0, alpha: 0, jam_kerja: 0, lembur: 0 });
          const workDays = data.working_days_in_period ?? data.working_days_in_month ?? 0;
          const expected = totalEmployees * workDays;
          const missed = totals.sakit + totals.izin + totals.cuti + totals.alpha;
          const attendanceRate = expected > 0 ? Math.max(0, Math.min(100, ((expected - missed) / expected) * 100)) : 0;
          return (
            <div className="grid grid-cols-12 gap-4">
              <div className="col-span-12 md:col-span-5 rounded-2xl bg-gradient-to-br from-blue-700 via-blue-800 to-emerald-800 text-white p-6 relative overflow-hidden">
                <div className="absolute -right-16 -top-16 w-56 h-56 rounded-full bg-emerald-400/20 blur-3xl"></div>
                <div className="relative">
                  <div className="text-[11px] uppercase tracking-widest text-blue-200">Tingkat Kehadiran</div>
                  <div className="font-display text-5xl mt-2">{attendanceRate.toFixed(1)}%</div>
                  <div className="text-xs text-blue-100 mt-1">
                    {expected > 0 ? `${expected - missed}/${expected} hari-orang hadir` : `Belum ada data`}
                  </div>
                  <div className="mt-4 pt-4 border-t border-white/10 text-xs flex items-center gap-4">
                    <span>👥 {totalEmployees} karyawan</span>
                    <span>📅 {workDays} hari kerja</span>
                    <span>🏢 {(data.departments || []).length} dept</span>
                  </div>
                </div>
              </div>
              <div className="col-span-6 md:col-span-2 rounded-2xl bg-red-50/60 border border-red-200 p-4">
                <div className="text-[10px] uppercase tracking-wider text-red-700 font-semibold">Alpha</div>
                <div className="font-display text-3xl mt-1 text-red-800">{totals.alpha}</div>
              </div>
              <div className="col-span-6 md:col-span-2 rounded-2xl bg-amber-50/60 border border-amber-200 p-4">
                <div className="text-[10px] uppercase tracking-wider text-amber-700 font-semibold">Sakit</div>
                <div className="font-display text-3xl mt-1 text-amber-800">{totals.sakit}</div>
              </div>
              <div className="col-span-6 md:col-span-3 rounded-2xl bg-white border border-stone-200/70 p-4">
                <div className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold">Izin / Cuti</div>
                <div className="font-display text-3xl mt-1 text-stone-900">{totals.izin + totals.cuti}</div>
                <div className="text-[10px] text-stone-500 mt-0.5">Izin {totals.izin} · Cuti {totals.cuti}</div>
              </div>
              <div className="col-span-12 md:col-span-6 rounded-2xl bg-white border border-stone-200/70 p-4">
                <div className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold">Total Jam Kerja</div>
                <div className="font-display text-2xl mt-1 text-stone-900 font-mono-num">{totals.jam_kerja.toFixed(1)}<span className="text-sm text-stone-500"> jam</span></div>
              </div>
              <div className="col-span-12 md:col-span-6 rounded-2xl bg-emerald-50/60 border border-emerald-100 p-4">
                <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold">Total Lembur</div>
                <div className="font-display text-2xl mt-1 text-emerald-800 font-mono-num">{totals.lembur.toFixed(1)}<span className="text-sm text-emerald-600"> jam</span></div>
              </div>
            </div>
          );
        })()}

        <Card className="p-3 text-xs text-stone-700 bg-stone-50/40 flex items-center gap-3 flex-wrap">
          <Calendar className="h-3.5 w-3.5 text-terra-600" />
          <span>Periode: <b>{data.period_start && data.period_end ? `${data.period_start} → ${data.period_end}` : (mode === "month" ? `${monthNames[month - 1]} ${year}` : data.month)}</b></span>
          <span>·</span>
          <span>Hari Kerja: <b>{data.working_days_in_period ?? data.working_days_in_month}</b></span>
          <span>·</span>
          <span>Total Karyawan: <b>{totalEmployees}</b></span>
          <span>·</span>
          <span>Departemen: <b>{(data.departments || []).length}</b></span>
          <span className="ml-auto text-stone-500 italic">ALPHA = tidak masuk tanpa keterangan</span>
        </Card>
        </>
      )}

      {loading && !data && <Card className="p-8 text-center text-sm text-stone-400">Memuat data…</Card>}
      {data && (data.departments || []).length === 0 && (
        <Card className="p-8 text-center text-sm text-stone-400">Tidak ada karyawan untuk bulan ini.</Card>
      )}

      {(data?.departments || []).map((dept) => {
        const isCol = !!collapsed[dept.department];
        return (
          <Card key={dept.department} className="p-0 overflow-hidden" data-testid={`rekap-dept-${dept.department}`}>
            <button
              onClick={() => setCollapsed((c) => ({ ...c, [dept.department]: !c[dept.department] }))}
              className="w-full flex items-center justify-between px-4 py-3 bg-emerald-100/70 hover:bg-emerald-100 border-b border-emerald-200"
              data-testid={`rekap-dept-header-${dept.department}`}
            >
              <div className="flex items-center gap-2">
                {isCol ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                <Users className="h-4 w-4 text-emerald-700" />
                <span className="font-semibold text-stone-900">{dept.department}</span>
                <Badge tone="success">{dept.count} karyawan</Badge>
              </div>
            </button>
            {!isCol && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-lime-300/80 text-stone-900 text-xs uppercase">
                    <tr>
                      <th className="text-center px-3 py-2 border border-stone-300 w-12">NO</th>
                      <th className="text-left px-3 py-2 border border-stone-300">Nama Karyawan</th>
                      <th className="text-center px-3 py-2 border border-stone-300">Total Hari</th>
                      <th className="text-center px-3 py-2 border border-stone-300">Total Jam Lembur</th>
                      <th className="text-center px-3 py-2 border border-stone-300">Total Jam Kerja</th>
                      <th className="text-center px-3 py-2 border border-stone-300">SAKIT</th>
                      <th className="text-center px-3 py-2 border border-stone-300">IZIN</th>
                      <th className="text-center px-3 py-2 border border-stone-300">CUTI</th>
                      <th className="text-center px-3 py-2 border border-stone-300">ALPHA</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dept.employees.map((e) => (
                      <tr key={e.employee_id} className="hover:bg-stone-50/40" data-testid={`rekap-row-${e.employee_id}`}>
                        <td className="text-center px-3 py-2 border border-stone-200 font-mono-num">{e.no}</td>
                        <td className="px-3 py-2 border border-stone-200">
                          {canViewDetail ? (
                            <button
                              onClick={() => openEmpDetail(e.employee_id)}
                              className="text-left hover:text-blue-700 hover:underline transition-colors"
                              data-testid={`rekap-emp-name-btn-${e.employee_id}`}
                              title="Klik untuk lihat detail karyawan"
                            >
                              <div className="font-medium text-stone-900">{e.name}</div>
                              {e.employee_code && <div className="text-[10px] text-stone-400 font-mono">{e.employee_code}</div>}
                            </button>
                          ) : (
                            <>
                              <div className="font-medium text-stone-900">{e.name}</div>
                              {e.employee_code && <div className="text-[10px] text-stone-400 font-mono">{e.employee_code}</div>}
                            </>
                          )}
                        </td>
                        <td className="text-center px-3 py-2 border border-stone-200 font-mono-num">{e.total_hari}</td>
                        <td className="text-center px-3 py-2 border border-stone-200 font-mono-num">{e.total_jam_lembur}</td>
                        <td className="text-center px-3 py-2 border border-stone-200 font-mono-num">{e.total_jam_kerja}</td>
                        <td className="text-center px-3 py-2 border border-stone-200 font-mono-num text-amber-700">{e.sakit || "—"}</td>
                        <td className="text-center px-3 py-2 border border-stone-200 font-mono-num text-blue-700">{e.izin || "—"}</td>
                        <td className="text-center px-3 py-2 border border-stone-200 font-mono-num text-teal-700">{e.cuti || "—"}</td>
                        <td className="text-center px-3 py-2 border border-stone-200 font-mono-num text-red-700" title={e.alpha_dates && e.alpha_dates.length ? `Tanggal alpha:\n${e.alpha_dates.join(', ')}` : ""} data-testid={`rekap-alpha-${e.employee_id}`}>{e.alpha || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        );
      })}

      {/* Iter v34by (#10) — Popup Detail Karyawan (scrollable + rapi) */}
      <Modal open={!!detailEmp || detailLoading} onClose={() => setDetailEmp(null)} title={detailEmp?.employee?.name || "Detail Karyawan"} size="xl">
        {detailLoading && <div className="p-6 text-center text-sm text-stone-400">Memuat…</div>}
        {detailEmp && (
          <div className="max-h-[75vh] overflow-y-auto pr-1 space-y-5" data-testid="rekap-emp-detail-modal">
            {/* ============ HEADER PROFIL ============ */}
            <div className="flex items-start gap-4 pb-4 border-b border-stone-200">
              <div className="w-20 h-20 rounded-full bg-stone-200 flex items-center justify-center text-stone-500 text-3xl font-semibold shrink-0 overflow-hidden ring-2 ring-white shadow-md">
                {detailEmp.employee.avatar_id ? (
                  <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${detailEmp.employee.avatar_id}`} alt="" className="w-full h-full object-cover" />
                ) : (
                  (detailEmp.employee.name || "?").charAt(0).toUpperCase()
                )}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-xl font-semibold text-stone-900">{detailEmp.employee.name}</h3>
                <p className="text-xs text-stone-500 font-mono mb-1.5">{detailEmp.employee.employee_code || "—"}</p>
                <div className="flex flex-wrap gap-1.5">
                  {detailEmp.employee.department && <Badge tone="success">{detailEmp.employee.department}</Badge>}
                  {detailEmp.employee.position && <Badge tone="terra">{detailEmp.employee.position}</Badge>}
                  {detailEmp.employee.department_category && <Badge tone="neutral">{detailEmp.employee.department_category}</Badge>}
                  {detailEmp.employee.profile_completed && <Badge tone="success" className="flex items-center gap-1"><ShieldCheck className="h-3 w-3" /> Profil Lengkap</Badge>}
                </div>
              </div>
            </div>

            {/* ============ SECTION: KONTAK & IDENTITAS ============ */}
            <section>
              <div className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold mb-3 flex items-center gap-1.5"><UserIcon className="h-3 w-3" /> Data Pribadi & Kontak</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                <DetailRow icon={Mail} label="Email" value={detailEmp.employee.email} />
                <DetailRow icon={Phone} label="Telepon" value={detailEmp.employee.phone} />
                <DetailRow icon={Fingerprint} label="NIK" value={detailEmp.employee.nik} mono />
                <DetailRow icon={Cake} label="Tgl. Lahir" value={detailEmp.employee.birth_date} />
                <DetailRow icon={MapPin} label="Alamat" value={detailEmp.employee.address} colSpan wrap />
              </div>
            </section>

            {/* ============ SECTION: KEPEGAWAIAN ============ */}
            <section>
              <div className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold mb-3 flex items-center gap-1.5"><Briefcase className="h-3 w-3" /> Kepegawaian</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                <DetailRow icon={Briefcase} label="Tgl. Bergabung" value={detailEmp.employee.join_date} />
                <DetailRow icon={ShieldCheck} label="Status" value={detailEmp.employee.status} />
                <DetailRow icon={Wallet} label="Gaji Pokok (Bulanan)" value={detailEmp.employee.gaji_pokok ? formatIDR(detailEmp.employee.gaji_pokok) : "—"} mono />
                <DetailRow icon={Wallet} label="Gaji Harian" value={detailEmp.employee.gaji_harian ? formatIDR(detailEmp.employee.gaji_harian) : "—"} mono />
                <DetailRow icon={ClockIcon} label="Overtime / Jam" value={detailEmp.employee.overtime_hourly_rate ? formatIDR(detailEmp.employee.overtime_hourly_rate) : "—"} mono />
                <DetailRow icon={Wallet} label="Uang Makan / Hari" value={detailEmp.employee.uang_makan_per_hari ? formatIDR(detailEmp.employee.uang_makan_per_hari) : "—"} mono />
                <DetailRow icon={Wallet} label="Tarif per Rit" value={detailEmp.employee.ritase_rate ? formatIDR(detailEmp.employee.ritase_rate) : "—"} mono />
                <DetailRow icon={ClockIcon} label="Max Lembur / Hari" value={detailEmp.employee.max_overtime_hours != null ? `${detailEmp.employee.max_overtime_hours} jam` : "—"} />
                <DetailRow icon={ShieldCheck} label="Kasbon/Hutang" value={detailEmp.employee.allow_debt ? "Diizinkan" : "Tidak Diizinkan"} />
                <DetailRow icon={ShieldCheck} label="Face ID" value={detailEmp.employee.has_face_setup ? `Setup (${detailEmp.employee.face_poses_count || 0} pose)` : "Belum"} />
              </div>
            </section>

            {/* ============ SECTION: REKENING BANK ============ */}
            <section>
              <div className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold mb-3 flex items-center gap-1.5"><CreditCard className="h-3 w-3" /> Rekening Bank</div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-2 text-sm">
                <DetailRow icon={CreditCard} label="Bank" value={detailEmp.employee.bank_name} />
                <DetailRow icon={UserIcon} label="A/n" value={detailEmp.employee.bank_account_holder} />
                <DetailRow icon={CreditCard} label="No. Rekening" value={detailEmp.employee.bank_account_number} mono />
              </div>
            </section>

            {/* ============ SECTION: RINGKASAN ABSENSI ============ */}
            <section>
              <div className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold mb-3 flex items-center gap-1.5">
                <Calendar className="h-3 w-3" /> Ringkasan Absensi · {detailEmp.period.start} → {detailEmp.period.end}
              </div>
              <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                {[
                  { label: "Hadir", val: detailEmp.attendance_summary.hadir, color: "emerald" },
                  { label: "Sakit", val: detailEmp.attendance_summary.sakit, color: "amber" },
                  { label: "Izin", val: detailEmp.attendance_summary.izin, color: "blue" },
                  { label: "Cuti", val: detailEmp.attendance_summary.cuti, color: "teal" },
                  { label: "Jam Kerja", val: detailEmp.attendance_summary.total_jam_kerja, color: "stone", suffix: "j" },
                  { label: "Lembur", val: detailEmp.attendance_summary.total_jam_lembur, color: "emerald", suffix: "j" },
                ].map((k) => (
                  <div key={k.label} className="border border-stone-200 rounded-lg p-2.5 text-center bg-stone-50/50">
                    <div className={`text-[10px] uppercase tracking-wider text-${k.color}-700 font-semibold`}>{k.label}</div>
                    <div className="font-display text-xl mt-0.5 text-stone-900 font-mono-num">{k.val}{k.suffix || ""}</div>
                  </div>
                ))}
              </div>
            </section>

            {/* ============ SECTION: DETAIL ABSENSI (SCROLLABLE) ============ */}
            {detailEmp.attendance_detail?.length > 0 && (
              <section>
                <div className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold mb-2 flex items-center gap-1.5"><ClockIcon className="h-3 w-3" /> Riwayat Absensi Periode Ini ({detailEmp.attendance_detail.length})</div>
                <div className="border border-stone-200 rounded-lg overflow-hidden">
                  <table className="w-full text-xs">
                    <thead className="bg-stone-100 text-[10px] uppercase tracking-wider text-stone-600">
                      <tr>
                        <th className="px-2 py-1.5 text-left">Tanggal</th>
                        <th className="px-2 py-1.5 text-left">Clock In</th>
                        <th className="px-2 py-1.5 text-left">Clock Out</th>
                        <th className="px-2 py-1.5 text-right">Jam</th>
                        <th className="px-2 py-1.5 text-right">Lembur</th>
                        <th className="px-2 py-1.5 text-left">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detailEmp.attendance_detail.map((r, i) => (
                        <tr key={i} className="border-t border-stone-100 hover:bg-stone-50">
                          <td className="px-2 py-1 font-mono text-[11px]">{r.date}</td>
                          <td className="px-2 py-1 font-mono text-[11px] text-stone-600">{r.clock_in ? String(r.clock_in).slice(11, 19) : "—"}</td>
                          <td className="px-2 py-1 font-mono text-[11px] text-stone-600">{r.clock_out ? String(r.clock_out).slice(11, 19) : "—"}</td>
                          <td className="px-2 py-1 text-right font-mono-num">{r.total_hours || 0}</td>
                          <td className="px-2 py-1 text-right font-mono-num text-emerald-700">{r.overtime_hours || 0}</td>
                          <td className="px-2 py-1"><Badge tone={r.status === "completed" ? "success" : "neutral"}>{r.status || "—"}</Badge></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )}

            {/* ============ SECTION: RIWAYAT IZIN/CUTI ============ */}
            {detailEmp.leaves_recent?.length > 0 && (
              <section>
                <div className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold mb-2 flex items-center gap-1.5"><FileText className="h-3 w-3" /> Izin/Cuti/Sakit Periode Ini ({detailEmp.leaves_recent.length})</div>
                <div className="space-y-1">
                  {detailEmp.leaves_recent.map((l, i) => (
                    <div key={i} className="border border-stone-200 rounded p-2 text-xs flex items-center gap-2 bg-stone-50/50">
                      <Badge tone={l.type === "sakit" ? "amber" : l.type === "cuti" ? "success" : "info"}>{l.type}</Badge>
                      <span className="font-mono text-stone-600">{l.date}</span>
                      {l.reason && <span className="text-stone-700 truncate flex-1">— {l.reason}</span>}
                      <Badge tone={l.status === "approved" ? "success" : "neutral"}>{l.status}</Badge>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* ============ SECTION: PAYROLL SNAPSHOT ============ */}
            {detailEmp.payroll_snapshot && (
              <section className="border border-emerald-200 bg-emerald-50/40 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp className="h-4 w-4 text-emerald-700" />
                  <span className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold">Payroll Terakhir · {detailEmp.payroll_snapshot.batch_label}</span>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <div className="text-[10px] uppercase text-stone-500">Gross</div>
                    <div className="font-mono-num font-semibold">{formatIDR(detailEmp.payroll_snapshot.gross_salary)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase text-stone-500">Net (Take-home)</div>
                    <div className="font-mono-num font-semibold text-emerald-800 text-lg">{formatIDR(detailEmp.payroll_snapshot.net_salary)}</div>
                  </div>
                </div>
              </section>
            )}

            <div className="sticky bottom-0 bg-white flex justify-end gap-2 pt-3 border-t border-stone-200 -mx-1 px-1">
              <Button variant="ghost" onClick={() => setDetailEmp(null)} data-testid="rekap-emp-detail-close">Tutup</Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

function escapeCSV(s) {
  if (s == null) return "";
  const str = String(s);
  if (/[",\n]/.test(str)) return `"${str.replace(/"/g, '""')}"`;
  return str;
}

// Iter v34by (#10) — Helper baris detail utk popup Rekap Departemen.
// icon: lucide component, label: string, value: any, mono: bool, colSpan: bool, wrap: bool.
function DetailRow({ icon: Icon, label, value, mono, colSpan, wrap }) {
  const empty = value === undefined || value === null || value === "" || value === false;
  return (
    <div className={`flex items-start gap-2 ${colSpan ? "md:col-span-2" : ""}`}>
      {Icon && <Icon className="h-3.5 w-3.5 text-stone-400 mt-0.5 shrink-0" />}
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-stone-400 leading-tight">{label}</div>
        <div className={`text-stone-800 ${mono ? "font-mono" : ""} ${wrap ? "whitespace-pre-wrap break-words" : "truncate"} ${empty ? "text-stone-400 italic" : ""}`}>
          {empty ? "—" : String(value)}
        </div>
      </div>
    </div>
  );
}

